import React, {useState} from "react";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import type {Editor} from "@tiptap/core";
import type {} from "@tiptap/extension-highlight";
import type {} from "@tiptap/extension-text-style";
import {getHighlightColorOptionGroups, getTextColorOptionGroups} from "./printoutToolbarColors";
import {updateSelectedPrintoutVariableMark} from "./printoutToolbarState";
import {ColorMenuButton, ColorPaletteGrid, ToolbarPopover} from "./toolbarControls";

interface Props {
    disabled: boolean;
    editor: Editor | null;
    highlightColor: string | null;
    textColor: string | null;
}

const ToolbarColorMenus = ({disabled, editor, highlightColor, textColor}: Props) => {

    const {t} = useTranslation();
    const theme = useTheme();
    const [highlightColorMenuAnchorEl, setHighlightColorMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [textColorMenuAnchorEl, setTextColorMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

    const textColorOptionGroups = getTextColorOptionGroups(theme, t);
    const highlightColorOptionGroups = getHighlightColorOptionGroups(theme, t);

    const setTextColor = (color: string | null) => {
        if (editor) {
            if (!updateSelectedPrintoutVariableMark(editor, "textStyle", color ? {color} : null)) {
                const chain = editor.chain().focus();

                if (color) {
                    chain.setColor(color).run();
                } else {
                    chain.unsetColor().removeEmptyTextStyle().run();
                }
            }
        }

        setTextColorMenuAnchorEl(null);
    };

    const setHighlightColor = (color: string | null) => {
        if (editor) {
            if (!updateSelectedPrintoutVariableMark(editor, "highlight", color ? {color} : null)) {
                const chain = editor.chain().focus();

                if (color) {
                    chain.setHighlight({color}).run();
                } else {
                    chain.unsetHighlight().run();
                }
            }
        }

        setHighlightColorMenuAnchorEl(null);
    };

    return (
        <>
            <ColorMenuButton
                activeColor={textColor}
                defaultIconColor={theme.palette.text.primary}
                disabled={disabled}
                icon={<FormatColorTextIcon fontSize="small"/>}
                label={t('Text color')}
                onClick={(event) => setTextColorMenuAnchorEl(event.currentTarget)}
            />
            <ColorMenuButton
                activeColor={highlightColor}
                disabled={disabled}
                icon={<FormatColorFillIcon fontSize="small"/>}
                label={t('Highlight color')}
                onClick={(event) => setHighlightColorMenuAnchorEl(event.currentTarget)}
            />

            <ToolbarPopover
                anchorEl={textColorMenuAnchorEl}
                onClose={() => setTextColorMenuAnchorEl(null)}
                open={Boolean(textColorMenuAnchorEl)}
            >
                <ColorPaletteGrid
                    groups={textColorOptionGroups}
                    onSelect={setTextColor}
                    selectedColor={textColor}
                />
            </ToolbarPopover>
            <ToolbarPopover
                anchorEl={highlightColorMenuAnchorEl}
                onClose={() => setHighlightColorMenuAnchorEl(null)}
                open={Boolean(highlightColorMenuAnchorEl)}
            >
                <ColorPaletteGrid
                    groups={highlightColorOptionGroups}
                    onSelect={setHighlightColor}
                    selectedColor={highlightColor}
                />
            </ToolbarPopover>
        </>
    );
};

export default ToolbarColorMenus;
